'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ISpeciesGrouping } from '@domain/species/types';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { TopSpeciesCTA } from '../TopSpeciesCTA/TopSpeciesCTA';
import { SpeciesGroupCard } from './components/SpeciesGroupCard/SpeciesGroupCard';
import { getSpeciesGroupCountryUrl, getSpeciesGroupRegionUrl } from '@domain/routes/urls';

const DEFAULT_MAX_VISIBLE_ITEMS_MOBILE = 3;
const DEFAULT_MAX_VISIBLE_ITEMS_DESKTOP = 6;
const SHOW_MORE_INCREMENT = 3;

interface IProps {
  appDownloadModalHeader?: string;
  countryCode: string;
  regionCode?: string;
  className?: string;
  id?: string;
  clickEventSourceId?: string;
  headingText: string;
  speciesGroups: ISpeciesGrouping[];
  nrOfVisibleItemsMobile?: number;
  nrOfVisibleItemsDesktop?: number;
  hasDownloadCTA?: boolean;
}

export const SpeciesGroupSection = ({
  appDownloadModalHeader = `Download the Fishbrain app to see all species from this area`,
  clickEventSourceId,
  countryCode,
  regionCode,
  id = 'species-group-section',
  headingText,
  speciesGroups,
  className,
  nrOfVisibleItemsMobile = DEFAULT_MAX_VISIBLE_ITEMS_MOBILE,
  nrOfVisibleItemsDesktop = DEFAULT_MAX_VISIBLE_ITEMS_DESKTOP,
  hasDownloadCTA = true,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [expandedCount, setExpandedCount] = useState(0);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);
  const { currentPage } = useAnalytics();

  const speciesCount = speciesGroups.length;
  const mobileVisible = nrOfVisibleItemsMobile + expandedCount;
  const desktopVisible = nrOfVisibleItemsDesktop + expandedCount;
  const hasMoreSpeciesMobile = mobileVisible < speciesCount;
  const hasMoreSpeciesDesktop = desktopVisible < speciesCount;
  const hasMoreSpecies = hasMoreSpeciesMobile || hasMoreSpeciesDesktop;
  const showMoreOnMobile = hasMoreSpeciesMobile || (hasDownloadCTA && !showDownloadCTA);
  const showMoreOnDesktop = hasMoreSpeciesDesktop || (hasDownloadCTA && !showDownloadCTA);
  const showMoreSpeciesButton = showMoreOnMobile || showMoreOnDesktop;

  // Filter duplicates by name
  const uniqueSpeciesGroups = speciesGroups.filter(
    (group, index, self) => self.findIndex(g => g.name === group.name) === index,
  );

  const handleShowMore = () => {
    if (hasMoreSpecies) {
      setExpandedCount(prev => prev + SHOW_MORE_INCREMENT);
    } else if (hasDownloadCTA) {
      // Show the CTA if there are no more species and CTA is enabled
      setShowDownloadCTA(true);
    }
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'SpeciesGroupSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {uniqueSpeciesGroups.map(({ name, externalId, image }, index) => {
          const imageUrl = image?.url;
          const imageWidth = image?.width;
          const imageHeight = image?.height;
          const speciesGroupUrl = regionCode
            ? getSpeciesGroupRegionUrl(countryCode, regionCode, externalId)
            : getSpeciesGroupCountryUrl(countryCode, externalId);
          return (
            <SpeciesGroupCard
              key={`species-${externalId}`}
              speciesId={externalId}
              testId={`species-card-${externalId}`}
              name={name}
              className="w-full"
              linkClassName={twMerge(
                index >= mobileVisible && 'hidden',
                index >= desktopVisible ? 'sm:hidden' : 'sm:block',
              )}
              speciesGroupUrl={speciesGroupUrl}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={clickEventSourceId}
              clickEventSource="TopSpeciesSection"
              useCustomLoader={false}
            />
          );
        })}
        {showMoreSpeciesButton && (
          <Button
            size="sm"
            className={twMerge(
              'mt-2 p-2',
              !showMoreOnMobile && 'hidden sm:block',
              !showMoreOnDesktop && 'sm:hidden',
            )}
            variant="textPrimary"
            onClick={handleShowMore}
          >
            Show more species
          </Button>
        )}

        {showDownloadCTA && hasDownloadCTA && (
          <TopSpeciesCTA onCTAClick={handleCTAClick} sourceId={clickEventSourceId} />
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="SpeciesGroupSection"
        appDownloadSourceId={clickEventSourceId}
        modalHeader={appDownloadModalHeader}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
